import type { StandingPromoConfig } from '~~/shared/utils/standingPromo'
import type { NewsletterCampaign, NewsletterDiscount } from '~~/app/utils/newsletterOffer'
import { discantoBrand } from './discanto/config'
import { prolesBrand } from './proles/config'
import { lavmagBrand } from './lavmag/config'
import { youlookBrand } from './youlook/config'

export interface BrandConfig {
  /** Musi zgadzać się z NUXT_PUBLIC_BRAND i z [data-brand] w themes.css */
  id: string
  name: string
  currency: string
  /**
   * Próg darmowej dostawy (PLN) pokazywany w UI koszyka — pasek postępu „kwalifikuje
   * się" oraz „Gratis" w podsumowaniu. Wartość PREZENTACYJNA; autorytatywny pozostaje
   * checkout (realna cena przewoźnika z PrestaShop). MUSI być zsynchronizowana z
   * backendowym `PS_SHIPPING_FREE_PRICE` danego sklepu — osobno per store/DB i per
   * środowisko (dev/prod mają własne DB). lavmag = 150, youlook = 159.
   */
  freeShippingThreshold: number
  /** Fallback; w runtime nadpisuje NUXT_SITE_URL */
  siteUrl: string
  /** Host CDN obrazów produktów */
  cdnHost: string
  /** Zewnętrzny serwis zwrotów „Wygodne Zwroty" — inny URL per sklep */
  returnsUrl: string
  /**
   * Moduł odstąpienia od umowy „Wygodne Zwroty" (Dyrektywa UE 2023/2673) —
   * ścieżka `/odstapienie` tego samego serwisu, osobna od formularza nadania
   * przesyłki (`returnsUrl`). Tu klient składa oświadczenie o odstąpieniu i
   * dopiero potem nadaje zwrot. Pusty = brak modułu (UI chowa przycisk).
   */
  returnsWithdrawalUrl: string
  /**
   * Adres, na który klient odsyła zwrot — tylko gdy różni się od siedziby
   * (`footer`). Brak = zwroty idą na adres siedziby, tak jak dotąd u lavmaga
   * i youlooka. Patrz getReturnsAddress().
   */
  returnsAddress?: { name: string, street: string, postalCode: string, city: string }
  // Hidy integracji „Wygodne Zwroty" (banner_hid / widget_hid) CELOWO nie są
  // tutaj — nie mają deterministycznego defaultu (WZ nadaje je per konto), więc
  // konfigurujemy je wyłącznie w inventory, jak GTM/Pixel/Segmentify:
  // NUXT_PUBLIC_RETURNS_WZ_BANNER_HID / NUXT_PUBLIC_RETURNS_WZ_WIDGET_HID.
  // Patrz useReturns().
  /**
   * `src` to logotyp na jasne tło. `srcInverted` jest opcjonalny i służy
   * miejscom o ciemnym tle (stopka): logotyp Proles Concept ma czarny napis,
   * który na grafitowej stopce byłby niewidoczny. Marki bez wersji odwróconej
   * pomijają to pole i wszędzie używają `src` — tak jak dotąd.
   *
   * `alt` to tekst alternatywny logo w nagłówku. Link logo ma `aria-label`,
   * więc dla czytnika ekranu pusty `alt` jest poprawny, ale audyty SEO
   * zgłaszają taki obrazek jako nieopisany — zgłosił to proles. Brak = pusty
   * `alt`, jak dotąd.
   */
  logo: { src: string, srcInverted?: string, alt?: string }
  /**
   * Własne fonty marki (woff2) do `<link rel="preload">` — tylko te, które marka
   * naprawdę renderuje na pierwszym ekranie. Brak = tylko wspólny preload Funnel
   * Sans z nuxt.config.ts, tak jak dotąd. Patrz useBrandHead().
   */
  fontPreload?: string[]
  favicon: { svg: string; ico: string; appleTouchIcon: string }
  /**
   * Dokąd prowadzi CTA pustych stanów („Zobacz sklep" w ulubionych, zamówieniach,
   * zwrotach, braku wyników wyszukiwania). Brak = dotychczasowy link do działu
   * „Kobieta" lavmaga — patrz shopEntryHref().
   */
  shopEntryHref?: string
  /**
   * Ikona aplikacji w modalu instalacji PWA i na stronie /offline. Brak = wspólna
   * `/pwa-icons/icon-192.png` (zestaw lavmaga) — patrz appIcon().
   */
  appIcon?: string
  /**
   * Zdjęcie banera na stronach kategorii (sklep sprzedażowy). Brak = mapa
   * działów i zapasowe `/images/home/hero.png` lavmaga (lavmag, youlook);
   * pusty łańcuch = wariant bez zdjęcia. Patrz useBanners().
   */
  categoryBanner?: string
  /**
   * Dopisek przy KAŻDEJ cenie produktu (karta, pasek przyklejony, kafle listingu,
   * szybki podgląd), który rozstrzyga netto/brutto — klient B2B (proles) inaczej
   * nie wie, którą cenę widzi. MUSI odpowiadać temu, co zwraca backend:
   * `price_amount` liczy się według wyświetlania cen grupy klienta
   * niezalogowanego (z podatkiem / bez podatku). Brak = cena bez dopisku, jak
   * dotąd (lavmag, youlook, discanto — sklepy B2C, gdzie cena brutto jest oczywista).
   */
  priceTaxNote?: string
  /**
   * Nazwa cookie z decyzją z banera zgód. Brak = `lavmag_cookie_consent`
   * (CONSENT_COOKIE_NAME): nazwę nadała pierwsza marka, a zmiana u lavmaga,
   * youlooka czy discanto unieważniłaby zapisane zgody — baner wróciłby do
   * wszystkich klientów. Nowa marka dostaje neutralną nazwę od startu, bo
   * nazwa cookie trafia do jej polityki prywatności.
   */
  consentCookieName?: string
  /**
   * Breadcrumb karty produktu pełną ścieżką z menu (dział › podkategoria),
   * a nie samą kategorią domyślną produktu. Brak/false = jak dotąd.
   */
  pdpBreadcrumbNavPath?: boolean
  /**
   * Drzewo „Kategorie" w filtrach listingu rozwija także ZAZNACZONĄ wartość,
   * nie tylko jej rodziców. Na stronie działu (zaznaczony sam dział) od razu
   * widać jego podkategorie. Brak = zaznaczony węzeł zostaje zwinięty.
   */
  filterTreeExpandSelected?: boolean
  /**
   * Układ galerii karty produktu na desktopie. Brak/'grid' = siatka 2 kolumn
   * portretowych kafli 3:4 (odzież: kilka ujęć modelki obok siebie).
   * 'main-thumbs' = jedno duże zdjęcie w kwadracie (do wysokości ekranu) +
   * miniatury pod nim; na mobile kwadratowa karuzela. Dla packshotów gadżetów
   * siatka dawała galerię wysoką na 1680–2640 px, głównie białej przestrzeni.
   */
  pdpGalleryLayout?: 'grid' | 'main-thumbs'
  /**
   * Galeria karty produktu i szybkiego podglądu podąża za wybranym kolorem:
   * zdjęcia tego koloru + wspólne (`Product.galleryItems`, `color_ids`
   * z /rest/productdetail), zob. app/utils/variantGallery. Kolor bez własnych
   * zdjęć albo produkt bez przypisań → wszystkie zdjęcia. Działa tam, gdzie
   * kolor wybiera się przyciskami (tryb katalogowy). Brak/false = galeria
   * zawsze ze wszystkich zdjęć, jak dotąd.
   */
  pdpGalleryFollowsColor?: boolean
  /**
   * Nazwa i SKU na karcie produktu (H1, okruch, „Szczegóły produktu i skład”,
   * zapytanie o wycenę) oraz nazwa w szybkim podglądzie podążają za wybranym
   * wariantem. EasyItem bierze nazwę i SKU produktu z kombinacji domyślnej
   * („BOSTON Bialy 3XL”), zob. app/utils/variantDetails. Działa tam, gdzie
   * wariant wybiera się przyciskami (tryb katalogowy). Brak/false = nazwa
   * i szczegóły zawsze z produktu, jak dotąd.
   */
  pdpDetailsFollowVariant?: boolean
  /**
   * Liczba kafli w rzędzie sekcji „Może Cię zainteresować" / „Inni oglądali
   * również" na karcie produktu (desktop). Brak = 3, jak dotąd.
   */
  pdpRecommendationColumns?: 3 | 4
  /**
   * Zmiana rozmiaru na karcie produktu zapisuje go w adresie (`?rozmiar=L`,
   * router.replace; w sklepie katalogowym także kolor i inne grupy —
   * `?kolor=czarny`). Każda taka zmiana to nawigacja → page_view w dataLayer
   * i Meta, więc marki z analityką (lavmag, youlook) zostają bez tego —
   * brak/false = adres się nie zmienia, jak dotąd. Odczyt `?rozmiar=` przy
   * wejściu na kartę działa u wszystkich.
   */
  pdpSizeInUrl?: boolean
  /**
   * Okrągła próbka koloru przed nazwą na przyciskach grupy kolorów (karta
   * produktu i szybki podgląd w trybie katalogowym). Dostawcy nie przysyłają
   * hexów, więc kolor wylicza mapa nazw (app/utils/colorSwatch); nazwa
   * nierozpoznana zostaje samym tekstem. Brak/false = same napisy, jak dotąd.
   */
  colorSwatches?: boolean
  footer: {
    address: string
    postalCode: string
    city: string
    country: string
    email: string
    /**
     * Imienne kontakty „Biura obsługi klienta" na stronie kontaktu, pod `email`
     * (np. opiekunowie klientów): imię nad mailem i — jeśli jest — telefonem tej
     * osoby. Brak = sam `email`. Stopka ich nie pokazuje.
     */
    serviceContacts?: Array<{ name: string; email: string; phone?: string }>
    phone: string
    copyrightName: string
    createdBy: string
    /** Pełna nazwa prawna podmiotu (administrator danych) — klauzule RODO */
    legalName: string
    /** Siedziba w miejscowniku do klauzuli „z siedzibą w …"; pusty = pomiń frazę */
    legalSeat: string
    /**
     * Dane rejestrowe pokazywane pod adresem siedziby na stronie kontaktu.
     * Opcjonalne, bo do tej pory żaden sklep ich nie renderował — youlook trzymał
     * je wyłącznie w komentarzu do configu. Pusty lub brak = wiersz się nie
     * pojawia, więc marki bez tych danych zostają bez zmian w UI.
     */
    taxId?: string
    regon?: string
    /**
     * Numer KRS — tylko spółki. Dziś żadna marka go nie ma (discanto od 16.10.2026
     * to JDG, bez KRS). Jak NIP/REGON: pusty lub brak = brak wiersza.
     */
    krs?: string
    /** Godziny obsługi klienta (pon.–pt.) pokazywane na stronie kontaktu — różne per sklep (lavmag 8:00–15:00, youlook 8:00–14:00) */
    serviceHours: string
    /** Linki kolumny „Kategorie" w stopce — różne per sklep */
    links: Array<{ label: string; href: string }>
  }
  social: { tiktok?: string; instagram?: string; facebook?: string }
  seo: {
    titleTemplate: string
    defaultTitle: string
    defaultDescription: string
    ogImage: string
    themeColor: string
  }
  pwa: {
    name: string
    shortName: string
    description: string
    /**
     * Ikony Web App Manifest — RÓŻNE per sklep (lavmag = /pwa-icons/*, youlook =
     * /brands/youlook/pwa-icons/*). Manifest jest serwowany dynamicznie per-brand
     * w runtime (server/routes/site.webmanifest.get.ts), bo jeden obraz Dockera
     * obsługuje oba sklepy — statyczny manifest z buildu zapiekłby brand domyślny.
     */
    icons: Array<{ src: string; sizes: string; type: string; purpose: string }>
    /** Screenshoty promptu instalacji — opcjonalne per sklep (pomijane, gdy brak assetów) */
    screenshots?: Array<{
      src: string
      sizes: string
      type: string
      form_factor: 'narrow' | 'wide'
    }>
    /**
     * Skróty aplikacji (przytrzymanie ikony na ekranie głównym). Brak = wspólne
     * skróty sklepu sprzedażowego (koszyk, konto, nowości) — patrz manifest.ts.
     * Sklep katalogowy (proles) ich nie ma: `/cart` i `/account` dają tam 404,
     * więc wskazuje własne trasy.
     */
    shortcuts?: Array<{ name: string; url: string }>
    /** Kategorie sklepów z aplikacjami. Brak = `shopping, lifestyle, fashion`. */
    categories?: string[]
  }
  /**
   * Loga „Akceptowane płatności" w koszyku — różne per sklep: lavmag = Przelewy24,
   * youlook = Tpay (BLIK wspólny — obsługiwany przez obie bramki). Wartość czysto
   * PREZENTACYJNA; autorytatywną listę metod płatności zwraca backend w checkoucie.
   */
  paymentLogos: Array<{ src: string; alt: string; width: number; height: number }>
  /** Warianty strukturalne — TYLKO gdy markup naprawdę się różni (patrz spec 3.6) */
  variants?: {
    newsletterLayout?: 'default' | 'youlook'
  }
  /**
   * Rabat obiecany w sekcji newslettera wariantu youlook („20% zniżki na pierwsze
   * zamówienie!"). MUSI zgadzać się z kodem z maila powitalnego w edrone. Brak = 20,
   * jak dotąd.
   */
  newsletterDiscount?: NewsletterDiscount
  /**
   * Kampania na landingu `/newsletter` (grafika z reklamy, hasło, tag edrone).
   * Brak = ogólny landing z treścią sekcji newslettera, jak dotąd.
   */
  newsletterCampaign?: NewsletterCampaign
  /**
   * Opinie o sklepie z eKomi/Zaufane.pl. `ekomiCertificateId` + `profileUrl` są jawne
   * (publiczna strona opinii). Klucze API (interface_id/password) NIE tu — tylko w
   * runtimeConfig (server). Brak sekcji = funkcja opinii wyłączona dla brandu.
   */
  reviews?: {
    ekomiCertificateId?: string
    profileUrl?: string
  }
  /**
   * Akcja „stałe kody rabatowe od wartości zamówienia" — kody, progi i kolory panelu
   * są RÓŻNE per sklep (lavmag = EXTRA*, youlook = MORE*), bo reguły koszyka żyją w
   * osobnych instancjach PrestaShop. Brak sekcji = marka nie prowadzi akcji (blok
   * kodów w koszyku/na karcie produktu się nie renderuje).
   *
   * Kolory belki u góry i popupu NIE są tutaj — te przychodzą z PrestaShop
   * (pstbannercountdownpro / creativepopup), żeby marketing mógł je zmienić bez deployu.
   */
  standingPromo?: StandingPromoConfig
  /**
   * Baner „Dostarczamy przesyłkę do Twojego kraju: Francja" dla odwiedzających z zagranicy.
   * Kraj odwiedzającego z Cloudflare (`CF-IPCountry`), kraje dostawy z backendu
   * (`/rest/delivery-countries` — przewoźnicy InPost International). Brak = baner wyłączony;
   * dla marki bez przewoźników zagranicznych i tak nie miałby czego pokazać.
   */
  deliveryCountryBanner?: boolean
  /**
   * Zwinięty panel wysuwany z creativepopup (pozycja left/right z czarnym paskiem) pokazuje
   * pasek o wysokości ETYKIETY + chevronu, a nie całej treści oferty. Na życzenie lavmaga
   * (Asana 1219036947001658: ~380 px czarnego pasa na kartach produktów na telefonie).
   * Brak/false = pasek na wysokość treści, jak dotąd — inne sklepy tego nie zgłaszały.
   */
  popupCompactTab?: boolean
  /**
   * Etykieta linków do `/regulamin-sklepu` (stopka). Ma się zgadzać z tytułem
   * dokumentu w CMS danego sklepu — proles nie sprzedaje i jego dokument to
   * „Regulamin serwisu internetowego", więc „Regulamin sklepu" wprowadzał
   * w błąd. Adres zostaje ten sam. Brak = „Regulamin sklepu" — patrz termsLabel().
   */
  termsLabel?: string
  /**
   * Menu mobilne: pozycja paska bez podkategorii (Nowości, Techniki
   * znakowania, linki statyczne) jest linkiem prosto do swojej strony, a nie
   * zakładką, która w treści szuflady pokazuje samą swoją nazwę. Zgłosił to
   * proles (Asana 1219579307523782). Brak/false = zakładka jak dotąd.
   */
  mobileNavDirectLinks?: boolean
  /**
   * Strona główna: sekcja „Ostatnio dodane" czyta to samo zapytanie o nowości
   * co zakładka „Nowości". Gdy WSZYSTKIE zakładki są nowościami (proles ma
   * tylko tę jedną), obie sekcje pokazują te same produkty — true chowa wtedy
   * „Ostatnio dodane". Brak/false = obie sekcje jak dotąd.
   */
  homeSkipDuplicateNewProducts?: boolean
  /**
   * Strona główna: sekcja pod hero (siatka 2×4) pokazuje NAJNOWSZE produkty
   * jednej kategorii pod podanym tytułem zamiast „Bestsellerów". `id` i `href`
   * muszą wskazywać tę samą kategorię w bazie sklepu (ID zmienia się tylko przy
   * pełnej wymianie katalogu). Pusta kategoria albo błąd zapytania = sekcja
   * wraca do „Bestsellerów". Brak = „Bestsellery", jak dotąd. Strona 404 dalej
   * pokazuje bestsellery.
   */
  homeFeaturedCategory?: { title: string, id: string, href: string }
  /**
   * Wyszukiwarka dopasowana do asortymentu marki. Brak sekcji (lub pola) =
   * zachowanie sklepu odzieżowego, jak dotąd u lavmaga, youlooka i discanto.
   */
  search?: {
    /**
     * Przykładowe frazy w polu wyszukiwania strony 404 („Czego szukasz? Np. …").
     * Brak = „sukienka, kurtka, buty". Wypisuj rzeczy, które katalog marki
     * naprawdę zwraca w wyszukiwarce.
     */
    examples?: string
    /**
     * Produkty w rzędzie wyników overlaya z lupki w nagłówku (desktop >1100 px;
     * węższe progi bez zmian). Brak = 4, z 8 wynikami. Przy 3 overlay pokazuje
     * 6 wyników — dwa pełne rzędy.
     */
    overlayColumns?: 3 | 4
  }
  /**
   * Minimalny nakład zapytania o wycenę (sklep katalogowy): nakład startowy
   * pozycji na liście zapytania i w formularzu wyceny oraz dolna granica w UI.
   * MUSI zgadzać się z obietnicą na stronie „O nas" (proles: „od 50 — sztuk
   * minimalnego nakładu"). Brak = 1 szt., jak dotąd. Tylko UI — backend
   * przyjmuje każdy dodatni nakład. Patrz quoteMinQuantity().
   */
  quoteMinQuantity?: number
}

export const DEFAULT_BRAND = 'lavmag'

export const BRANDS: Record<string, BrandConfig> = {
  lavmag: lavmagBrand,
  youlook: youlookBrand,
  proles: prolesBrand,
  discanto: discantoBrand,
  // Alias przejściowy. Sklep chodził pod kryptonimem `gadzety` i tak ma go
  // jeszcze wpisane inventory (NUXT_PUBLIC_BRAND). Gdyby zabrakło tego wpisu,
  // getBrand() spadłby na markę domyślną i sklep pokazałby się jako lavmag —
  // po wdrożeniu inventory z `proles` alias można usunąć.
  gadzety: prolesBrand,
}

export function getBrand(id: string | undefined | null): BrandConfig {
  if (id && BRANDS[id]) return BRANDS[id]
  return BRANDS[DEFAULT_BRAND]!
}

/** Adres zwrotu marki — własny (`returnsAddress`) albo adres siedziby ze stopki. */
export function getReturnsAddress(brand: BrandConfig): { name: string, street: string, postalCode: string, city: string } {
  return brand.returnsAddress ?? {
    name: brand.footer.legalName,
    street: brand.footer.address,
    postalCode: brand.footer.postalCode,
    city: brand.footer.city,
  }
}

/**
 * Link CTA pustych stanów. Domyślny wskazuje ID kategorii z bazy lavmaga — tak
 * było od początku i lavmag/youlook/proles zostają przy nim bez zmian (na youlooku
 * i proles ten link prowadzi do 404, do poprawienia osobno). Marka z własną bazą
 * ustawia `shopEntryHref`.
 */
export function shopEntryHref(brand: BrandConfig): string {
  return brand.shopEntryHref || '/16190-odziez-damska'
}

/** Etykieta linku do regulaminu — własna marki albo dotychczasowa. */
export function termsLabel(brand: BrandConfig): string {
  return brand.termsLabel || 'Regulamin sklepu'
}

/**
 * Ikona aplikacji (192 px) dla modalu instalacji i strony offline. Domyślna to
 * wspólny zestaw `/pwa-icons/` — lavmag go używa, a youlook dostaje go do dziś
 * zamiast własnej ikony (do poprawienia osobno). Marka z własnym zestawem ikon
 * ustawia `appIcon`.
 */
export function appIcon(brand: BrandConfig): string {
  return brand.appIcon || '/pwa-icons/icon-192.png'
}

/** Minimalny nakład zapytania o wycenę marki — 1 szt., gdy marka go nie ustawia. */
export function quoteMinQuantity(brand: BrandConfig): number {
  const minimum = brand.quoteMinQuantity

  return typeof minimum === 'number' && Number.isInteger(minimum) && minimum > 1 ? minimum : 1
}
